/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { techdocsApiRef } from '@backstage/plugin-techdocs-react';
import { snykApiRef } from 'backstage-plugin-snyk';
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
  makeStyles,
} from '@material-ui/core';
import BuildIcon from '@material-ui/icons/Build';
import DescriptionIcon from '@material-ui/icons/Description';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SecurityIcon from '@material-ui/icons/Security';
import RateReviewIcon from '@material-ui/icons/RateReview';
import { fetchPrCounts } from './prCounts';
import { useHomeScope } from './HomeScopeContext';
import { useViewAs } from '../viewAs/ViewAsContext';

const TECHDOCS_REF = 'backstage.io/techdocs-ref';
const AUTO_DISCOVERED = 'm42.dev/auto-discovered';
const SNYK_ORG_ID = 'snyk.io/org-id';

// Top-N caps bound the fan-out of per-entity lazy fetches so a 100-service owner
// doesn't trigger 100 Snyk / TechDocs calls on Home load. Sorted by name for
// determinism (alphabetical) so the cap picks the same N each render.
const TECHDOCS_CAP = 20;
const SNYK_CAP = 20;
// 30 days; older techdocs builds are flagged stale (a real signal for cleanup).
const STALE_DAYS = 30;
// StorageApi-backed cache TTL. Snyk runs daily scans, so a quarter-day cache is
// safe. TechDocs build status is even more stable. Persists across page reloads
// (StorageApi is per-user, per-browser; not per-tab) -- "checking Snyk..." only
// runs once per scope per quarter-day, not every navigation.
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h

type Stamped<T> = { ts: number; v: T };



const cacheBucket = 'home.pending';
const readCache = <T,>(storage: any, key: string): T | null => {
  const hit = storage.forBucket(cacheBucket).snapshot(key);
  const val = hit?.value as Stamped<T> | undefined;
  if (!val) return null;
  if (Date.now() - val.ts > CACHE_TTL_MS) return null;
  return val.v;
};
const writeCache = <T,>(storage: any, key: string, v: T): void => {
  storage.forBucket(cacheBucket).set(key, { ts: Date.now(), v });
};

const useStyles = makeStyles(theme => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  count: {
    fontWeight: 600,
    minWidth: '1.5em',
    textAlign: 'right',
  },
  loadingIcon: {
    color: theme.palette.text.secondary,
  },
  empty: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    paddingTop: theme.spacing(1),
  },
}));

type Row = {
  key: string;
  icon: React.ReactNode;
  text: React.ReactNode;
  count: number | null; // null = still loading
  href?: string;
  // In-page action instead of navigation (href becomes a no-op anchor).
  onClick?: (e: React.MouseEvent) => void;
};

// Sort entities by name so the top-N cap is deterministic across renders.
const stableSort = (items: Entity[]): Entity[] =>
  [...items].sort((a, b) => a.metadata.name.localeCompare(b.metadata.name));

const hasTechdocs = (e: Entity) =>
  !!e.metadata.annotations?.[TECHDOCS_REF];

const hasSnyk = (e: Entity) =>
  !!e.metadata.annotations?.[SNYK_ORG_ID];

const isAutoDiscovered = (e: Entity) =>
  e.metadata.annotations?.[AUTO_DISCOVERED] === 'true';

const scopeKey = (entities: Entity[], suffix: string): string =>
  entities.map(e => stringifyEntityRef(e)).sort().join('|') + '#' + suffix;

export const PendingWorkCardBody = () => {
  const { scopedEntities, effectiveOwnerRefs } = useHomeScope();
  const ownerRefs = effectiveOwnerRefs;
  const { viewAsLogin } = useViewAs();
  const classes = useStyles();
  const techdocsApi = useApi(techdocsApiRef);
  const storageApi = useApi(storageApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Pending PR reviews: direct review requests + requests to a team the user
  // belongs to (GitHub expands review-requested:<login> server-side). Shares
  // the /counts round-trip with the PR card via fetchPrCounts. null =
  // loading; -1 = failed (row omitted).
  const [reviewCount, setReviewCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    setReviewCount(null);
    fetchPrCounts(discoveryApi, fetchApi, viewAsLogin)
      .then(v => { if (!cancelled) setReviewCount(v.reviewRequested); })
      .catch(() => { if (!cancelled) setReviewCount(-1); });
    return () => { cancelled = true; };
  }, [viewAsLogin, discoveryApi, fetchApi]);
  // SnykApi is optional in case the plugin failed to register (defensive --
  // useApi throws if unbound; we fall back to disabling the metric).
  let snykApi: any = null;
  try { snykApi = useApi(snykApiRef); } catch { /* not registered */ }

  // Catalog signals: instant, derived from scopedEntities.
  const needsCatalogInfo = useMemo(
    () => scopedEntities.filter(isAutoDiscovered).length,
    [scopedEntities],
  );
  const missingDocsSetup = useMemo(
    () => scopedEntities.filter(e => !hasTechdocs(e)).length,
    [scopedEntities],
  );
  // Components in scope that DO have a snyk org-id annotation (the only ones
  // we can scan); used by the lazy Snyk count and to gate the "scan" row.
  const snykScannable = useMemo(
    () => stableSort(scopedEntities.filter(hasSnyk)),
    [scopedEntities],
  );
  // Components with docs that we can check build status on.
  const docsCheckable = useMemo(
    () => stableSort(scopedEntities.filter(hasTechdocs)),
    [scopedEntities],
  );

  // Lazy: docs build status (capped). Returns {stale, broken}.
  const [docsStatus, setDocsStatus] = useState<
    { stale: number; broken: number } | null
  >(null);
  useEffect(() => {
    let cancelled = false;
    setDocsStatus(null);
    if (docsCheckable.length === 0) {
      setDocsStatus({ stale: 0, broken: 0 });
      return () => { cancelled = true; };
    }
    const sample = docsCheckable.slice(0, TECHDOCS_CAP);
    const key = scopeKey(sample, 'techdocs');
    const cached = readCache<{ stale: number; broken: number }>(storageApi, key);
    if (cached) {
      setDocsStatus(cached);
      return () => { cancelled = true; };
    }
    (async () => {
      const cutoff = Date.now() - STALE_DAYS * 24 * 60 * 60 * 1000;
      const results = await Promise.all(
        sample.map(async e => {
          try {
            const ref = {
              kind: e.kind,
              namespace: e.metadata.namespace ?? 'default',
              name: e.metadata.name,
            };
            const meta: any = await techdocsApi.getTechDocsMetadata(ref);
            const ts = meta?.build_timestamp ?? meta?.buildTimestamp;
            if (typeof ts === 'number') {
              return ts < cutoff ? 'stale' : 'ok';
            }
            // No timestamp -> treat as broken (built but missing metadata).
            return 'broken';
          } catch {
            // 404 / fetch error -> docs configured but never built.
            return 'broken';
          }
        }),
      );
      if (cancelled) return;
      const next = {
        stale: results.filter(r => r === 'stale').length,
        broken: results.filter(r => r === 'broken').length,
      };
      writeCache(storageApi, key, next);
      setDocsStatus(next);
    })();
    return () => { cancelled = true; };
  }, [docsCheckable, ownerRefs, techdocsApi, storageApi]);

  // Lazy: Snyk critical+high count across scoped components (capped).
  const [snykCriticalHigh, setSnykCriticalHigh] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSnykCriticalHigh(null);
    if (!snykApi || snykScannable.length === 0) {
      setSnykCriticalHigh(0);
      return () => { cancelled = true; };
    }
    const sample = snykScannable.slice(0, SNYK_CAP);
    const key = scopeKey(sample, 'snyk');
    const cached = readCache<number>(storageApi, key);
    if (cached !== null) {
      setSnykCriticalHigh(cached);
      return () => { cancelled = true; };
    }
    (async () => {
      let total = 0;
      for (const e of sample) {
        const orgId = e.metadata.annotations?.[SNYK_ORG_ID];
        if (!orgId) continue;
        try {
          const projects = await snykApi.getCompleteProjectsListFromAnnotations(
            orgId,
            e.metadata.annotations ?? {},
            true,
          );
          for (const p of projects as Array<{ id: string }>) {
            try {
              const issues: any =
                await snykApi.listAllAggregatedIssues(orgId, p.id);
              const counts = snykApi.getIssuesCount(
                issues?.data ?? issues ?? [],
              );
              total += (counts?.critical ?? 0) + (counts?.high ?? 0);
            } catch { /* per-project failure -> skip */ }
          }
        } catch { /* per-entity failure -> skip */ }
        if (cancelled) return;
      }
      if (cancelled) return;
      writeCache(storageApi, key, total);
      setSnykCriticalHigh(total);
    })();
    return () => { cancelled = true; };
  }, [snykScannable, ownerRefs, snykApi, storageApi]);

  // Deep-link helpers: catalog URL contract verified live against the running
  // catalog (16 Jun 2026):
  //   filters[kind]=component      singular
  //   filters[user]=all|owned      override (default 'owned' would double-filter)
  //   filters[tags]=<tag>          singular even for the tag set (Backstage normalizes)
  //   filters[owners]=<groupRef>   REPEAT this key per ref for OR semantics --
  //                                array-bracket form is normalized to repeated keys.
  // One filters[owners] key per effective owner ref + filters[user]=all so
  // the catalog filters by the SAME ref set the card counted with. The
  // catalog's own filters[user]=owned is claims-based (direct memberships
  // only, no sub-team expansion) and undercounts vs the card -- it remains
  // only as a fallback when we have no refs at all.
  const scopeClause = (() => {
    if (ownerRefs.length === 0) return 'filters[user]=owned';
    const ownersParams = ownerRefs
      .map(s => `filters[owners]=${encodeURIComponent(s)}`)
      .join('&');
    return `filters[user]=all&${ownersParams}`;
  })();
  const tagLink = (tag: string) =>
    `/catalog?filters[kind]=component&${scopeClause}&filters[tags]=${encodeURIComponent(tag)}`;

  const rows: Row[] = [];
  // Reviews lead: blocking a teammate outranks the team's own tech debt.
  if (reviewCount === null) {
    rows.push({
      key: 'reviews-loading',
      icon: <RateReviewIcon fontSize="small" className={classes.loadingIcon} />,
      text: 'checking review requests…',
      count: null,
    });
  } else if (reviewCount > 0) {
    rows.push({
      key: 'reviews',
      icon: <RateReviewIcon fontSize="small" />,
      text: viewAsLogin
        ? 'pull requests await their review'
        : 'pull requests await your review',
      count: reviewCount,
      // One source of truth: switch the Open-pull-requests card to its
      // "Pending my review" mode (same number, same backend query) instead
      // of linking out to GitHub.
      href: '#open-pull-requests',
      onClick: e => {
        e.preventDefault();
        storageApi.forBucket('home').set('prCardMode', 'review');
        document
          .getElementById('open-pull-requests')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
    });
  }
  if (needsCatalogInfo > 0) {
    rows.push({
      key: 'catalog-info',
      icon: <BuildIcon fontSize="small" />,
      text: 'components need a catalog-info.yaml',
      count: needsCatalogInfo,
      href: tagLink('needs-catalog-info'),
    });
  }
  if (missingDocsSetup > 0) {
    rows.push({
      key: 'docs-setup',
      icon: <DescriptionIcon fontSize="small" />,
      text: 'missing docs setup',
      count: missingDocsSetup,
      href: tagLink('needs-docs'),
    });
  }
  // Stale / broken docs (lazy)
  if (docsStatus === null) {
    rows.push({
      key: 'docs-status-loading',
      icon: <ScheduleIcon fontSize="small" className={classes.loadingIcon} />,
      text: 'checking docs build status…',
      count: null,
    });
  } else if (docsStatus.stale + docsStatus.broken > 0) {
    rows.push({
      key: 'docs-status',
      icon: <ScheduleIcon fontSize="small" />,
      text:
        docsStatus.stale > 0 && docsStatus.broken > 0
          ? `stale (${docsStatus.stale}) or broken (${docsStatus.broken}) docs builds`
          : docsStatus.stale > 0
          ? 'stale docs builds (>30d)'
          : 'broken docs builds',
      count: docsStatus.stale + docsStatus.broken,
      href: '/docs',
    });
  }
  // Snyk critical+high (lazy)
  if (snykApi) {
    if (snykCriticalHigh === null) {
      rows.push({
        key: 'snyk-loading',
        icon: <SecurityIcon fontSize="small" className={classes.loadingIcon} />,
        text: 'checking Snyk…',
        count: null,
      });
    } else if (snykCriticalHigh > 0) {
      rows.push({
        key: 'snyk',
        icon: <SecurityIcon fontSize="small" />,
        text: 'critical or high vulnerabilities',
        count: snykCriticalHigh,
        // Snyk has-critical-vulns tag is a follow-up (needs a scheduled
        // aggregator -- N x K Snyk calls per refresh would melt the API).
        // Until then, the row scopes the catalog to ownership and the user
        // opens an entity to see its Snyk tab.
        href: `/catalog?filters[kind]=component&${scopeClause}`,
      });
    }
  }

  // Caps surfaced honestly so a 50-service owner knows they're seeing a sample.
  const capNote = (() => {
    const exceeded: string[] = [];
    if (docsCheckable.length > TECHDOCS_CAP) exceeded.push(`docs: top ${TECHDOCS_CAP}`);
    if (snykApi && snykScannable.length > SNYK_CAP) exceeded.push(`snyk: top ${SNYK_CAP}`);
    return exceeded.length > 0
      ? `Sampling ${exceeded.join(' · ')} by name`
      : null;
  })();

  return (
    <>
      {rows.length === 0 ? (
        <Typography className={classes.empty}>
          Nothing pending in this scope. Owned components are documented,
          built, and scanned.
        </Typography>
      ) : (
        <Box className={classes.list}>
          {rows.map((r, i) => (
            <React.Fragment key={r.key}>
              {i > 0 && <Divider light />}
              <Box className={classes.row}>
                {r.icon}
                {/* span, not the default <p>: the loading state nests
                    CircularProgress (a div), which is invalid inside <p>
                    (validateDOMNesting); as a flex child the layout is
                    identical. */}
                <Typography component="span" className={classes.count}>
                  {r.count === null ? (
                    <CircularProgress size={14} thickness={6} />
                  ) : (
                    r.count > 99 ? '99+' : r.count
                  )}
                </Typography>
                {r.href ? (
                  <Link to={r.href} onClick={r.onClick}>{r.text}</Link>
                ) : (
                  <Typography component="span">{r.text}</Typography>
                )}
              </Box>
            </React.Fragment>
          ))}
          {capNote && (
            <Typography className={classes.empty} variant="caption">
              {capNote}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};
